import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewRef } from '@angular/core';
import { DomSanitizer, Meta, SafeHtml, SafeUrl, Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { InitializerService, IResponseLower, PrebootService, SeoService, UiHelperService } from 'core-max-lib';
import { Subscription } from 'rxjs';
import { ILcImage, MagazineService } from '../../interfaces/magazine-service.interface';
import { IAdditionalInfoContainerResult, IMagazineArticle, IMagazineCategoryArticleResult, IOrderedItem, IQuestionsAndAnswersContainerResult } from '../../models/magazine-result';
import { AdditionalInfoContainerResult, AdditionalInfoResult } from '../../models/additional-info-result';

declare var $: any;

@Component({
    selector: 'lib-magazine-article',
    templateUrl: './general-article.component.html',
    styleUrls: ['./general-article.component.scss']
})
export class GeneralArticleComponent implements OnInit, OnDestroy, AfterViewInit {

    constructor(
        @Inject(DOCUMENT) private readonly document: any,
        public sanitizer: DomSanitizer,
        public magazineService: MagazineService,
        public router: Router,
        private activatedRoute: ActivatedRoute,
        private titleSrv: Title,
        private meta: Meta,
        private prebootSvc: PrebootService,
        private uiHelper: UiHelperService,
        private initializer: InitializerService,
        private seoService: SeoService
    ) {
        this.isMobileRequest = this.initializer.getClientContext().IsMobileRequest;
    }

    @ViewChild('moreInfoSection')
    moreInfoSection: ElementRef;
    @ViewChild('moreInfoVC', { read: ViewContainerRef })
    moreInfoVC: ViewContainerRef;
    @ViewChild('moreInfoT', { read: TemplateRef })
    moreInfoT: TemplateRef<any>;
    moreInfoViewRef: ViewRef;

    public text: SafeHtml;
    public title: string;
    public subtitle: string;
    public date: string;
    public extraInfo: string;
    public desktopImg: ILcImage;
    public mobileImg: ILcImage;
    public faq: IQuestionsAndAnswersContainerResult;
    public safeMailToUrl: SafeUrl;
    public magazineCategory: IMagazineCategoryArticleResult;
    public cta: boolean;
    public ctaText: string;
    public ctaUrl: string;
    public ctaTarget: string;
    public ctaBackgroundColor: string;
    public ctaTextColor: string;
    public playbuzz: boolean;
    public playbuzzDiv: SafeHtml;
    public showImageInArticlePage: boolean;
    public textAndVideos: IOrderedItem<any>[];
    public disclaimerText: string;

    public interestingArticles: Array<IMagazineArticle> = [];
    public interestingArticlesContainer: IAdditionalInfoContainerResult;

    isMobile = false;
    noMainPicture = false;
    subs: Subscription[] = [];

    private isMobileRequest;

    ngAfterViewInit() {
        if (this.playbuzz && this.playbuzzDiv) {
            this.loadPlaybuzzScript();
        }
    }

    ngOnInit() {
        this.isMobile = this.magazineService.isMobile();
        this.magazineService.insertStylesFix();

        this.subs.push(this.activatedRoute.params.subscribe((params: Params) => {
            const requestedArticle = params['magazine-article'];
            const requestedCategory = params['product-name'];

            this.magazineService.getGeneralArticle(requestedArticle, requestedCategory)
                .subscribe((res: IResponseLower<IMagazineCategoryArticleResult>) => {
                    if (res.returnCode !== 0) {
                        if (requestedCategory == 'jobs') {
                            this.magazineService.navigateToJobs();
                        }
                        else {
                            this.magazineService.redirectToErrorPage();
                        }
                        return;
                    }

                    const category = res.result;
                    if (!category || !category.article) {
                        this.magazineService.redirectToErrorPage();
                        return;
                    }

                    const article = category.article;
                    this.magazineCategory = category;
                    this.faq = article.questionsAndAnswersContainer;
                    if (this.faq && this.faq.questionsAndAnswers) {
                        const faqCore = this.faq.questionsAndAnswers.slice(0, 5).map(qa => {return {QuestionText: qa.questionText, AnswerText: qa.answerText}});
                        this.seoService.setQuestionAndAnswers(faqCore);
                    }
                    this.title = article.title;
                    this.subtitle = article.subtitle;
                    this.text = this.sanitizer.bypassSecurityTrustHtml(
                        article.text
                    );
                    this.showImageInArticlePage =
                        article.showImageInArticlePage;
                    this.desktopImg = article.desktopImage;
                    this.mobileImg = article.mobileImage;
                    this.noMainPicture = !this.showImageInArticlePage || !this.mobileImg.url || !this.desktopImg.url;
                    this.date = article.date;
                    this.extraInfo = article.extraInfo;
                    this.cta = article.cta;
                    this.ctaText = article.ctaText;
                    this.ctaUrl = article.ctaUrl;
                    this.ctaTarget = article.ctaTarget;
                    this.ctaBackgroundColor = article.ctaBackgroundColor;
                    this.ctaTextColor = article.ctaTextColor;
                    this.textAndVideos = article.textAndVideos;
                    this.disclaimerText = article.disclaimerText;
                    if (article.playbuzz && article.playbuzzDiv) {
                        this.playbuzz = article.playbuzz;
                        this.playbuzzDiv = this.sanitizer.bypassSecurityTrustHtml(article.playbuzzDiv);
                        if (this.uiHelper.isBrowser) {
                            const playbuzz = (window as any).Playbuzz;
                            if (playbuzz) {
                                playbuzz.render();
                            }
                        }
                        this.prebootSvc.transitionComplete.subscribe(stable => {
                            if (stable) {
                                this.loadPlaybuzzScript();
                            }
                        });
                    }
                    this.safeMailToUrl = this.generateSafeMailToUrl(
                        this.title,
                        window.location.href
                    );

                    // more-articles
                    this.interestingArticles = [];
                    if (!category.articles || category.articles.length < 3) {
                        return;
                    }
                    const usedIndexes = [];
                    for (
                        let i = this.getRandomInt(
                            category.articles.length
                        );
                        this.interestingArticles.length < 3;
                        i = this.getRandomInt(category.articles.length)
                    ) {
                        if (
                            category.articles[i].pageUrl.toLowerCase() !==
                            params['magazine-article'].toLowerCase() &&
                            category.articles[i].title !==
                            params['magazine-article'] &&
                            usedIndexes.indexOf(i) < 0 // we haven't used this index yet
                        ) {
                            this.interestingArticles.push(
                                category.articles[i]
                            );

                            usedIndexes.push(i);
                        }
                    }

                    if (this.interestingArticles.length === 3) {
                        this.interestingArticlesContainer = new AdditionalInfoContainerResult();
                        this.interestingArticlesContainer.additionalInfoMainTitle =
                            res.result.moreInfoTitle;
                        this.interestingArticlesContainer.additionalInfoLinkText = res
                            .result.moreInfoLinkText
                            ? res.result.moreInfoLinkText
                            : 'לכל הכתבות';
                        this.interestingArticlesContainer.additionalInfoLinkUrl =
                            res.result.moreInfoLinkUrl;
                        this.interestingArticlesContainer.additionalInfoCategoryName =
                            category.name
                        this.interestingArticlesContainer.additionalInfoList = [];
                        for (let i = 0; i < 3; i++) {
                            const interestingArticle = this.interestingArticles[
                                i
                            ];
                            const additionalInfoArticle = new AdditionalInfoResult();
                            additionalInfoArticle.additionalInfoTitle =
                                interestingArticle.title;
                            additionalInfoArticle.additionalInfoText =
                                interestingArticle.subtitle;
                            additionalInfoArticle.additionalInfoLinkUrl =
                                interestingArticle.pageUrl == null
                                    ? this.router
                                        .createUrlTree([
                                            category.urlName,
                                            'magazine',
                                            interestingArticle.title
                                        ])
                                        .toString()
                                    : this.router
                                        .createUrlTree([
                                            category.urlName,
                                            'magazine',
                                            interestingArticle.pageUrl
                                        ])
                                        .toString();
                            additionalInfoArticle.additionalInfoLinkText = interestingArticle.moreInfoReadMoreText
                                ? interestingArticle.moreInfoReadMoreText
                                : 'קרא עוד';
                            additionalInfoArticle.additionalInfoImage =
                                interestingArticle.moreInfoImage;

                            this.interestingArticlesContainer.additionalInfoList.push(
                                additionalInfoArticle
                            );
                        }
                    } else {
                        this.magazineService.redirectToErrorPage();
                        return;
                    }
                });
        }));

        this.createRteImgStyle();
    }

    generateSafeMailToUrl(subject: string, body: string): SafeUrl {
        let href = 'javascript:location.href = ';
        let url: string = 'mailto:?to=&subject=' + subject + '&body=' + body;
        url = encodeURI(url);
        url = `"` + url + `"`;
        href += url;
        return this.sanitizer.bypassSecurityTrustUrl(href);
    }

    ngOnDestroy(): void {
        this.magazineService.removeStylesFix();
        this.subs.forEach(sub => sub.unsubscribe())
    }

    navigateToProductPage(e) {
        e.preventDefault();
        console.log(" : " + this.magazineCategory.productPageUrl);
        return this.router.navigate([this.magazineCategory.productPageUrl]);
    }

    private loadScript(html: string): Promise<boolean> {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.innerHTML = html;

            script.onload = () => {
                resolve(true);
            };

            script.onerror = () => {
                reject(false);
            };

            document.head.appendChild(script);
        });
    }

    async loadPlaybuzzScript(): Promise<any> {
        const loads = [];
        loads.push(
            this.loadScript(
                '(function(d, s, id) {var js,fjs = d.getElementsByTagName(s)[0];if (d.getElementById(id)) return;js = d.createElement(s);js.id = id;js.src = \'https://embed.ex.co/sdk.js\';fjs.parentNode.insertBefore(js, fjs);})(document, \'script\', \'playbuzz-sdk\');'
            )
        );

        if (loads.length) {
            await Promise.all(loads);
        }
    }

    createRteImgStyle() {
        if (
            this.document.head.querySelector(`style[id="magazineRteImgStyle"]`)
        ) {
            return;
        }

        const styleElement = this.document.head.appendChild(
            this.document.createElement('style')
        ) as HTMLStyleElement;

        styleElement.setAttribute('type', 'text/css');
        styleElement.setAttribute('id', 'magazineRteImgStyle');
        styleElement.innerHTML = `@media (max-width: 575px) {
            .magazine .description-wrapper .description p img {
                max-width: 100%;
                width: auto !important;
                height: auto !important;
            }
        }
        .magazine .description-wrapper .description table tbody { border: none; }`;
    }

    getRandomInt(max) {
        return Math.floor(Math.random() * Math.floor(max));
    }
}
